import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { WidgetUser } from '@/config';

const WIDGET_SCRIPT_URL = (import.meta.env
  .VITE_REACT_APP_AMLBOT_WIDGET_URL as string)!;

/**
 * How long to wait for the widget to report itself ready before giving up on
 * it. `script.onerror` only covers the embed script failing to download — it
 * says nothing about the iframe behind it failing to boot (widget server down,
 * appId the widget app rejects), which would otherwise leave callers loading
 * forever.
 */
const WIDGET_READY_TIMEOUT_MS = 15000;

type WidgetMethod = 'init' | 'open' | 'close' | 'toggle' | 'sendData';

interface AmlBotWidgetParams {
  appId: string | null;
  user: WidgetUser | null;
}

interface AmlBotWidgetInstance {
  open: () => void;
  close: () => void;
  toggle: () => void;
  sendData: (params: { hash: string; network: string }) => void;
  isReady: () => boolean;
  isLoading: boolean;
  loadFailed: boolean;
}

/**
 * Loads the AMLBot widget embed script for a given appId and returns a typed
 * instance for calling widget methods (open, close, toggle, sendData).
 *
 * Nothing is injected until `appId` is known, because the embed script reads
 * `window.amlBotSettings` once at load and mutating it afterwards does not
 * apply retroactively. While `appId` is null the widget reports as loading, so
 * callers keep showing their loading state rather than a fallback.
 *
 * `init` is called as soon as `appId` and `user` are both known, and
 * deliberately NOT gated on readiness: the embed only creates its iframe when
 * `init` runs, and that iframe is what reports WIDGET_READY. Waiting for ready
 * before calling init deadlocks. Calling it early is the supported path — the
 * embed stub queues the call and drains the queue once the script loads.
 *
 * The widget is fully cleaned up on unmount and whenever `appId` changes.
 */
export const useAmlBotWidget = ({
  appId,
  user,
}: AmlBotWidgetParams): AmlBotWidgetInstance => {
  const isCleanedUp = useRef(false);
  const [ready, setReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!appId) return;

    isCleanedUp.current = false;

    (window as any).amlBotSettings = {
      appId,
      placement: 'bottom-left',
      mode: 'inline',
      containerId: 'amlbot-inline-container',
    };

    if (!(window as any).AMLBotWidget) {
      (window as any).AMLBotWidget = function (...args: any[]) {
        (window as any).AMLBotWidget.q.push(args);
      };
      (window as any).AMLBotWidget.q = [] as any[];
    }

    const existingScript = document.querySelector(
      `script[src="${WIDGET_SCRIPT_URL}"]`,
    );
    if (!existingScript) {
      const script = document.createElement('script');
      script.async = true;
      script.src = WIDGET_SCRIPT_URL;
      script.onerror = () => {
        if (!isCleanedUp.current) {
          setLoadFailed(true);
        }
      };
      document.head.appendChild(script);
    }

    const readyTimer = window.setTimeout(() => {
      if (!isCleanedUp.current) {
        setLoadFailed(true);
      }
    }, WIDGET_READY_TIMEOUT_MS);

    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'WIDGET_READY') {
        window.clearTimeout(readyTimer);
        setReady(true);
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      isCleanedUp.current = true;
      setReady(false);
      setLoadFailed(false);

      window.clearTimeout(readyTimer);
      window.removeEventListener('message', onMessage);

      if (typeof (window as any).AMLBotWidget === 'function') {
        (window as any).AMLBotWidget('close');
      }

      const scriptEl = document.querySelector(
        `script[src="${WIDGET_SCRIPT_URL}"]`,
      );
      if (scriptEl) {
        scriptEl.remove();
      }

      ['amlbot-wrapper', 'amlbot-toggle-button'].forEach((id) => {
        document.getElementById(id)?.remove();
      });

      delete (window as any).amlBotSettings;
      delete (window as any).AMLBotWidget;
    };
  }, [appId]);

  const call = useCallback((method: WidgetMethod, params?: any): any => {
    if (isCleanedUp.current) return;
    return (window as any).AMLBotWidget?.(method, params);
  }, []);

  useEffect(() => {
    if (!appId || !user) return;

    call('init', {
      username: user.username,
      externalId: user.externalId,
    });
  }, [appId, user, call]);

  const widget = useMemo<AmlBotWidgetInstance>(
    () => ({
      open: () => call('open'),
      close: () => call('close'),
      toggle: () => call('toggle'),
      sendData: (params) => call('sendData', params),
      isReady: () => ready,
      isLoading: !ready && !loadFailed,
      loadFailed,
    }),
    [ready, loadFailed, call],
  );

  return widget;
};
